import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Button,
  Stack,
  Alert,
  Grid,
  Paper,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getShippingRates, updateShippingRate } from '../api/shippingApi';

interface Location {
  id: number;
  name: string;
  city: string;
  province: string;
  flat_shipping_rate: number | string;
  free_shipping_minimum: number | string; // ✅ new field
  wholesale_free_shipping_minimum: number | string;

}

const ShippingRatesPage = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getShippingRates();
      setLocations(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load shipping rates');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
  index: number,
  key: "flat_shipping_rate" | "free_shipping_minimum" | "wholesale_free_shipping_minimum",
  value: string
) => {
  const updated = [...locations];
  updated[index][key] = value;
  setLocations(updated);
};


  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      for (const location of locations) {
        await updateShippingRate(
  location.id,
  parseFloat(location.flat_shipping_rate as string),
  parseFloat(location.free_shipping_minimum as string),
  parseFloat((location.wholesale_free_shipping_minimum as string) || "0")
);

      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to update shipping rates');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 4 }}>
        <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ShippingIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Shipping Rates
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadRates}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ minWidth: 150 }}
            >
              {saving ? 'Saving...' : 'Save All'}
            </Button>
          </Stack>
        </Stack>
        
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Manage flat shipping rates for different locations
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(false)}>
          Shipping rates updated successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {locations.map((loc, index) => (
          <Grid item xs={12} sm={6} md={4} key={loc.id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 3,
              boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 20px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <LocationIcon color="primary" />
                  <Typography variant="h6" fontWeight="600">
                    {loc.name}
                  </Typography>
                </Stack>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {loc.city}, {loc.province}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <MoneyIcon color="primary" />
                  <Typography variant="subtitle2">Shipping Rate</Typography>
                </Stack>
                
                <TextField
                  fullWidth
                  label="Flat Rate"
                  type="number"
                  value={loc.flat_shipping_rate}
onChange={(e) => handleChange(index, "flat_shipping_rate", e.target.value)}
                  inputProps={{ 
                    min: 0, 
                    step: 0.01,
                    'aria-label': `Shipping rate for ${loc.name}`
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography color="text.secondary" sx={{ mr: 1 }}>$</Typography>
                    ),
                  }}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper
                    }
                  }}
                />
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
  <MoneyIcon color="primary" />
  <Typography variant="subtitle2">Free Shipping Minimum</Typography>
</Stack>
<TextField
  fullWidth
  label="Minimum for Free Shipping (Retail)"
  type="number"
  value={loc.free_shipping_minimum}
  onChange={(e) => handleChange(index, "free_shipping_minimum", e.target.value)}
  inputProps={{ min: 0, step: 0.01 }}
  InputProps={{
    startAdornment: (
      <Typography color="text.secondary" sx={{ mr: 1 }}>$</Typography>
    ),
  }}
  variant="outlined"
  size="small"
  sx={{ mt: 1 }}
/>
<TextField
  fullWidth
  label="Minimum for Free Shipping (Wholesale)"
  type="number"
  value={loc.wholesale_free_shipping_minimum ?? 0}
  onChange={(e) => handleChange(index, "wholesale_free_shipping_minimum", e.target.value)}
  inputProps={{ min: 0, step: 0.01 }}
  helperText="Applies to wholesale orders. 0 = no free shipping for wholesale."
  InputProps={{
    startAdornment: (
      <Typography color="text.secondary" sx={{ mr: 1 }}>$</Typography>
    ),
  }}
  variant="outlined"
  size="small"
  sx={{ mt: 1.5 }}
/>

              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontSize: '1rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
            }
          }}
        >
          {saving ? 'Saving Changes...' : 'Save All Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default ShippingRatesPage;